import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./ApplicationHeadOwners.css";

import API from "../config/API";

function ApplicationHeadOwners() {
  const navigate = useNavigate();

  // =========================================================
  // STATE
  // =========================================================

  const [pgOwners, setPgOwners] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const [currentPage, setCurrentPage] = useState(1);

  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [approvingId, setApprovingId] = useState(null);

  // Success popup
  const [successPopup, setSuccessPopup] = useState({
    show: false,
    message: "",
  });

  const rowsPerPage = 10;

  // =========================================================
  // API ENDPOINTS
  // =========================================================

  const GET_PG_OWNERS_API = `${API.V1}/head/getPgOwnersData`;

  const UPDATE_STATUS_API = `${API.V1}/head/isActivePgStatus`;

  // Approve API
  const APPROVE_PG_OWNER_API = `${API.V1}/head/approveByPgOwner`;

  // =========================================================
  // SHOW SUCCESS POPUP
  // =========================================================

  const showSuccessPopup = (message) => {
    setSuccessPopup({
      show: true,
      message,
    });

    setTimeout(() => {
      setSuccessPopup({
        show: false,
        message: "",
      });
    }, 2000);
  };

  // =========================================================
  // LOAD PG OWNERS
  // =========================================================

  useEffect(() => {
    loadPgOwners();
  }, []);

  const loadPgOwners = async () => {
    try {
      setLoading(true);

      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const response = await axios.get(GET_PG_OWNERS_API, {
        headers: {
          Authorization: `Bearer ${user?.jwtToken}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data?.statusCode === 200) {
        setPgOwners(
          Array.isArray(response.data?.data) ? response.data.data : [],
        );
      } else {
        setPgOwners([]);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.clear();

        alert("Session Expired. Please login again.");

        setTimeout(() => {
          navigate("/");
        }, 1000);

        return;
      }

      console.error("Get PG Owners Error:", error);

      setPgOwners([]);
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // UPDATE ACTIVE / INACTIVE STATUS
  // =========================================================

  const updateStatus = async (pgOwnerId, status) => {
    try {
      setUpdatingStatusId(pgOwnerId);

      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const payload = {
        pgOwnerId: pgOwnerId,
        isActiveStatus: status,
      };

      console.log("Updating PG Owner Status:", payload);

      const response = await axios.post(UPDATE_STATUS_API, payload, {
        headers: {
          Authorization: `Bearer ${user?.jwtToken}`,
          "Content-Type": "application/json",
        },
      });

      console.log("Status Update Response:", response.data);

      if (response.data?.statusCode === 200) {
        await loadPgOwners();
      } else {
        alert(response.data?.message || "Unable to update PG Owner status.");
      }
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.clear();

        alert("Session Expired. Please login again.");

        setTimeout(() => {
          navigate("/");
        }, 1000);

        return;
      }

      console.error("Update PG Owner Status Error:", error);

      alert(error.response?.data?.message || "Unable to update status.");
    } finally {
      setUpdatingStatusId(null);
    }
  };

  // =========================================================
  // APPROVE PG OWNER
  // =========================================================

  const approvePgOwner = async (pgOwnerId) => {
    try {
      setApprovingId(pgOwnerId);

      const user = JSON.parse(localStorage.getItem("user") || "{}");

      // =====================================================
      // REQUEST PAYLOAD
      // =====================================================

      const payload = {
        appAdminId: user?.userId,
        pgAdminId: pgOwnerId,
        isActiveStatus: true,
      };

      console.log("Approve PG Owner Payload:", payload);

      // =====================================================
      // POST APPROVAL API
      // =====================================================

      const response = await axios.post(APPROVE_PG_OWNER_API, payload, {
        headers: {
          Authorization: `Bearer ${user?.jwtToken}`,
          "Content-Type": "application/json",
        },
      });

      console.log("Approve PG Owner Response:", response.data);

      // =====================================================
      // SUCCESS
      // =====================================================

      if (response.data?.statusCode === 200) {
        // Show green popup for 2 seconds
        showSuccessPopup(
          response.data?.message || "PG Owner approved successfully!",
        );

        // Refresh data
        await loadPgOwners();
      } else {
        alert(response.data?.message || "Unable to approve PG Owner.");
      }
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.clear();

        alert("Session Expired. Please login again.");

        setTimeout(() => {
          navigate("/");
        }, 1000);

        return;
      }

      console.error("Approve PG Owner Error:", error);

      alert(error.response?.data?.message || "Unable to approve PG Owner.");
    } finally {
      setApprovingId(null);
    }
  };

  // =========================================================
  // PG VIEW
  // =========================================================

  const handlePgNameClick = (pgOwnerId) => {
    navigate("/pgOwnerView", {
      state: {
        pgOwnerId,
      },
    });
  };

  // =========================================================
  // LOCATIONS
  // =========================================================

  const locations = useMemo(() => {
    return [
      ...new Set(pgOwners.map((item) => item.pgLocation).filter(Boolean)),
    ];
  }, [pgOwners]);

  // =========================================================
  // PG TYPES
  // =========================================================

  const pgTypes = useMemo(() => {
    return [...new Set(pgOwners.map((item) => item.pgType).filter(Boolean))];
  }, [pgOwners]);

  // =========================================================
  // FILTER
  // =========================================================

  const filteredData = useMemo(() => {
    return pgOwners.filter((item) => {
      const searchText = search.toLowerCase().trim();

      const matchesSearch =
        !searchText ||
        item.pgName?.toLowerCase().includes(searchText) ||
        item.pgOwnerName?.toLowerCase().includes(searchText) ||
        item.pgOwnerEmailId?.toLowerCase().includes(searchText) ||
        item.pgOwnerContactNo?.toString().includes(searchText) ||
        item.pgUniqueId?.toLowerCase().includes(searchText);

      const matchesLocation =
        !locationFilter || item.pgLocation === locationFilter;

      const matchesType = !typeFilter || item.pgType === typeFilter;

      return matchesSearch && matchesLocation && matchesType;
    });
  }, [pgOwners, search, locationFilter, typeFilter]);

  // =========================================================
  // PAGINATION
  // =========================================================

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const currentRows = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  const changePage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // =========================================================
  // DATE / TIME
  // =========================================================

  const formatDateTime = (value) => {
    if (!value) {
      return "-";
    }

    try {
      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return value;
      }

      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      return value;
    }
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div className="applicationHeadContainer">
      {/* ===================================================
          SUCCESS POPUP
      ==================================================== */}

      {successPopup.show && (
        <div className="approveSuccessPopup">
          <div className="approveSuccessIcon">✓</div>

          <div className="approveSuccessContent">
            <strong>Success</strong>

            <span>{successPopup.message}</span>
          </div>
        </div>
      )}

      {/* ===================================================
          HEADER
      ==================================================== */}

      <div className="pageHeader">
        <div className="headerContent">
          <div className="headerIcon">🏢</div>

          <div>
            <h2>PG Owners</h2>

            <p>Manage registered PG owners, approval and account status.</p>
          </div>
        </div>

        <div className="headerCount">
          <span>Total PGs : </span>

          <strong>{filteredData.length}</strong>
        </div>
      </div>

      {/* ===================================================
          FILTERS
      ==================================================== */}

      <div className="filterCard">
        <div className="filterInputWrapper">
          <span className="searchIcon">🔍</span>

          <input
            type="text"
            placeholder="Search PG name, owner, email, contact or ID..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);

              setCurrentPage(1);
            }}
          />
        </div>

        <select
          value={locationFilter}
          onChange={(e) => {
            setLocationFilter(e.target.value);

            setCurrentPage(1);
          }}
        >
          <option value="">All Locations</option>

          {locations.map((location) => (
            <option key={location} value={location}>
              {location}
            </option>
          ))}
        </select>

        <select
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value);

            setCurrentPage(1);
          }}
        >
          <option value="">All PG Types</option>

          {pgTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </div>

      {/* ===================================================
          TABLE
      ==================================================== */}

      {loading ? (
        <div className="loadingCard">
          <div className="loadingSpinner"></div>

          <span>Loading PG Owners...</span>
        </div>
      ) : (
        <div className="tableCard">
          <div className="tableWrapper">
            <table>
              <thead>
                <tr>
                  <th>S.No</th>

                  <th>PG Name</th>

                  <th>Owner</th>

                  <th>Contact</th>

                  <th>Floors</th>

                  <th>Location</th>

                  <th>Status</th>

                  <th>Approval</th>
                </tr>
              </thead>

              <tbody>
                {currentRows.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="noData">
                      <div className="emptyState">
                        <div className="emptyIcon">📭</div>

                        <strong>No Records Found</strong>

                        <span>Try changing your search or filters.</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  currentRows.map((item, index) => (
                    <tr key={item.pgOwnerId}>
                      {/* S.NO */}

                      <td className="serialNumber">
                        {(currentPage - 1) * rowsPerPage + index + 1}
                      </td>

                      {/* PG NAME */}

                      <td>
                        <button
                          type="button"
                          className="pgNameLink"
                          onClick={() => handlePgNameClick(item.pgOwnerId)}
                        >
                          {item.pgName || "-"}

                          <span className="linkArrow">→</span>
                        </button>
                      </td>

                      {/* OWNER */}

                      <td>
                        <div className="ownerCell">
                          <div className="ownerAvatar">
                            {item.pgOwnerName?.charAt(0)?.toUpperCase() || "P"}
                          </div>

                          <span>{item.pgOwnerName || "-"}</span>
                        </div>
                      </td>

                      {/* CONTACT */}

                      <td>{item.pgOwnerContactNo || "-"}</td>

                      {/* FLOORS */}

                      <td>
                        <span className="floorBadge">
                          🏢 {item.requestedFloorCount || 0}
                        </span>
                      </td>

                      {/* LOCATION */}

                      <td>
                        <span className="locationText">
                          📍 {item.pgLocation || "-"}
                        </span>
                      </td>

                      {/* ACTIVE / INACTIVE */}

                      <td>
                        <div className="statusCell">
                          <div className="statusRadio">
                            <label
                              className={
                                item.pgActiveStatus === true
                                  ? "activeRadio"
                                  : ""
                              }
                            >
                              <input
                                type="radio"
                                name={`status-${item.pgOwnerId}`}
                                checked={item.pgActiveStatus === true}
                                disabled={updatingStatusId === item.pgOwnerId}
                                onChange={() =>
                                  updateStatus(item.pgOwnerId, true)
                                }
                              />

                              <span>Active</span>
                            </label>

                            <label
                              className={
                                item.pgActiveStatus === false
                                  ? "inactiveRadio"
                                  : ""
                              }
                            >
                              <input
                                type="radio"
                                name={`status-${item.pgOwnerId}`}
                                checked={item.pgActiveStatus === false}
                                disabled={updatingStatusId === item.pgOwnerId}
                                onChange={() =>
                                  updateStatus(item.pgOwnerId, false)
                                }
                              />

                              <span>Inactive</span>
                            </label>
                          </div>

                          {updatingStatusId === item.pgOwnerId && (
                            <div
                              className="statusTime"
                              style={{
                                color: "#2563EB",
                              }}
                            >
                              Updating status...
                            </div>
                          )}

                          {updatingStatusId !== item.pgOwnerId && (
                            <div
                              className={
                                item.pgActiveStatus === true
                                  ? "statusTime activeTime"
                                  : "statusTime inactiveTime"
                              }
                            >
                              {item.pgActiveStatus === true ? (
                                <>
                                  🕒 Active since{" "}
                                  {formatDateTime(item.activeSince)}
                                </>
                              ) : (
                                <>
                                  🕒 Inactive since{" "}
                                  {formatDateTime(item.inactiveSince)}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </td>

                      {/* =================================================
                            APPROVAL
                        ================================================== */}

                      <td>
                        {item.isApprovedByApplicationHead === true ? (
                          <span className="approvedBadge">
                            <span className="badgeDot"></span>
                            Approved
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="approveBtn"
                            disabled={approvingId === item.pgOwnerId}
                            onClick={() => approvePgOwner(item.pgOwnerId)}
                          >
                            {approvingId === item.pgOwnerId
                              ? "Approving..."
                              : "✓ Approve"}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===================================================
          PAGINATION
      ==================================================== */}

      {!loading && filteredData.length > 0 && (
        <div className="paginationContainer">
          <div className="paginationInfo">
            Showing <strong>{(currentPage - 1) * rowsPerPage + 1}</strong> to{" "}
            <strong>
              {Math.min(currentPage * rowsPerPage, filteredData.length)}
            </strong>{" "}
            of <strong>{filteredData.length}</strong>
          </div>

          <div className="paginationButtons">
            <button
              onClick={() => changePage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              ← Previous
            </button>

            <span className="pageNumber">
              Page {currentPage} of {totalPages}
            </span>

            <button
              onClick={() => changePage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {/* ===================================================
          INLINE POPUP CSS
      ==================================================== */}

      <style>
        {`
          .approveSuccessPopup {
            position: fixed;
            top: 24px;
            right: 24px;
            z-index: 99999;
            min-width: 320px;
            max-width: 450px;
            display: flex;
            align-items: center;
            gap: 14px;
            padding: 15px 18px;
            border-radius: 14px;
            background: linear-gradient(
              135deg,
              #15803d,
              #16a34a
            );
            color: #ffffff;
            box-shadow:
              0 12px 35px rgba(22,163,74,.35);
            animation:
              approvePopupSlideIn .3s ease-out;
          }

          .approveSuccessIcon {
            width: 34px;
            height: 34px;
            min-width: 34px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            background: rgba(255,255,255,.2);
            border: 2px solid rgba(255,255,255,.55);
            font-size: 19px;
            font-weight: 800;
          }

          .approveSuccessContent {
            display: flex;
            flex-direction: column;
            gap: 3px;
          }

          .approveSuccessContent strong {
            font-size: 14px;
            font-weight: 800;
          }

          .approveSuccessContent span {
            font-size: 13px;
            opacity: .95;
          }

          @keyframes approvePopupSlideIn {
            from {
              opacity: 0;
              transform: translateX(30px);
            }

            to {
              opacity: 1;
              transform: translateX(0);
            }
          }

          @media (max-width: 600px) {
            .approveSuccessPopup {
              left: 15px;
              right: 15px;
              top: 15px;
              min-width: auto;
            }
          }
        `}
      </style>
    </div>
  );
}

export default ApplicationHeadOwners;
